import { useEffect, useMemo, useState } from 'react';
import Decimal from 'decimal.js';
import { Banknote, CreditCard, Smartphone, Building2, Wallet, Plus, X, Check, QrCode } from 'lucide-react';
import { CxDialog } from '@/cajita/overlay';
import type { PaymentInput, PaymentMethod } from './sales.api';

const METHODS: Array<{ value: PaymentMethod; label: string; icon: typeof Banknote; color: string }> = [
  { value: 'CASH', label: 'Efectivo', icon: Banknote, color: '#22C55E' },
  { value: 'YAPE', label: 'Yape', icon: Smartphone, color: '#6D28D9' },
  { value: 'PLIN', label: 'Plin', icon: Smartphone, color: '#0EA5E9' },
  { value: 'CARD', label: 'Tarjeta', icon: CreditCard, color: '#2563EB' },
  { value: 'TRANSFER', label: 'Transfer.', icon: Building2, color: '#0F766E' },
];

const fmt = (n: number | string): string =>
  'S/ ' + Number(n).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface PaymentDraft extends PaymentInput {
  id: string;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  total: number;
  onConfirm: (params: { payments: PaymentInput[]; paymentTerm: 'CONTADO' | 'CREDITO'; dueDate?: string }) => void;
  isPending: boolean;
  customerHasRuc: boolean;
}

export function CheckoutSheet({ open, onOpenChange, total, onConfirm, isPending, customerHasRuc }: Props): JSX.Element | null {
  const [payments, setPayments] = useState<PaymentDraft[]>([]);
  const [paymentTerm, setPaymentTerm] = useState<'CONTADO' | 'CREDITO'>('CONTADO');
  const [dueDate, setDueDate] = useState('');

  useEffect(() => {
    if (open && payments.length === 0 && total > 0) {
      setPayments([{ id: crypto.randomUUID(), method: 'CASH', amount: total, receivedAmount: total }]);
    }
    if (!open) {
      setPayments([]);
      setPaymentTerm('CONTADO');
      setDueDate('');
    }
  }, [open, total]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPaid = useMemo(() => payments.reduce((acc, p) => acc.plus(p.amount || 0), new Decimal(0)).toNumber(), [payments]);
  const balance = total - totalPaid;
  const totalCash = useMemo(
    () => payments.filter((p) => p.method === 'CASH').reduce((acc, p) => acc.plus(p.receivedAmount ?? p.amount), new Decimal(0)).toNumber(),
    [payments],
  );
  const cashAmount = useMemo(
    () => payments.filter((p) => p.method === 'CASH').reduce((acc, p) => acc.plus(p.amount), new Decimal(0)).toNumber(),
    [payments],
  );
  const change = Math.max(0, totalCash - cashAmount);

  const canConfirm =
    !isPending &&
    payments.length >= (paymentTerm === 'CONTADO' ? 1 : 0) &&
    (paymentTerm === 'CREDITO' ? !!dueDate && balance > 0 : balance <= 0.001) &&
    payments.every((p) => p.amount > 0);

  const updatePayment = (id: string, patch: Partial<PaymentDraft>): void => setPayments((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const removePayment = (id: string): void => setPayments((prev) => prev.filter((p) => p.id !== id));
  const addPayment = (): void => {
    const remaining = Math.max(0, balance);
    setPayments((prev) => [...prev, { id: crypto.randomUUID(), method: 'CASH', amount: remaining, receivedAmount: remaining }]);
  };

  const handleConfirm = (): void => {
    onConfirm({ payments: payments.map(({ id: _id, ...p }) => p), paymentTerm, dueDate: paymentTerm === 'CREDITO' ? dueDate : undefined });
  };

  if (!open) return null;

  return (
    <CxDialog
      title="Cobrar venta"
      sub={`Total a cobrar · ${fmt(total)}`}
      icon={<Wallet size={20} />}
      iconTone="success"
      width={480}
      onClose={() => onOpenChange(false)}
      footer={
        <>
          <button className="btn btn-secondary grow" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancelar
          </button>
          <button className="btn btn-primary grow" onClick={handleConfirm} disabled={!canConfirm}>
            <Check size={18} />
            {isPending ? 'Procesando…' : 'Confirmar y emitir'}
          </button>
        </>
      }
    >
      <div className="col gap16">
        <div className="row between aic" style={{ padding: '14px 16px', background: 'var(--canvas)', borderRadius: 12 }}>
          <span className="muted fw6">Total a cobrar</span>
          <span className="tnum fw8" style={{ fontSize: 24 }}>
            {fmt(total)}
          </span>
        </div>

        <div className="field">
          <label className="label">Condición</label>
          <div className="seg" style={{ width: '100%' }}>
            <button className={paymentTerm === 'CONTADO' ? 'on' : ''} style={{ flex: 1 }} onClick={() => setPaymentTerm('CONTADO')}>
              Contado
            </button>
            <button
              className={paymentTerm === 'CREDITO' ? 'on' : ''}
              style={{ flex: 1 }}
              disabled={!customerHasRuc}
              title={!customerHasRuc ? 'Crédito solo para clientes con RUC' : undefined}
              onClick={() => customerHasRuc && setPaymentTerm('CREDITO')}
            >
              Crédito
            </button>
          </div>
        </div>

        {paymentTerm === 'CREDITO' && (
          <div className="field">
            <label className="label">Fecha límite de cobro</label>
            <input className="input" type="date" value={dueDate} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setDueDate(e.target.value)} />
            <div className="t-xs muted">Si el cliente abona ahora, regístralo abajo. El saldo quedará pendiente.</div>
          </div>
        )}

        <div className="col gap10">
          {payments.map((p) => (
            <PaymentRow key={p.id} payment={p} onChange={(patch) => updatePayment(p.id, patch)} onRemove={payments.length > 1 ? () => removePayment(p.id) : undefined} />
          ))}
          <button className="btn btn-ghost btn-sm" onClick={addPayment} style={{ alignSelf: 'flex-start' }}>
            <Plus size={15} />
            Agregar otro pago
          </button>
        </div>

        <div className="card card-pad" style={{ background: 'var(--canvas)' }}>
          <div className="col gap8">
            <div className="row between t-sm">
              <span className="muted">Total venta</span>
              <span className="tnum">{fmt(total)}</span>
            </div>
            <div className="row between t-sm">
              <span className="muted">Pagado</span>
              <span className="tnum">{fmt(totalPaid)}</span>
            </div>
            {paymentTerm === 'CREDITO' && balance > 0 && (
              <div className="row between t-sm fw6" style={{ color: 'var(--warning)' }}>
                <span>Saldo pendiente</span>
                <span className="tnum">{fmt(balance)}</span>
              </div>
            )}
            {change > 0 && (
              <div className="row between aic fw7" style={{ paddingTop: 8, borderTop: '1px dashed var(--border)', color: '#15803D' }}>
                <span>Vuelto</span>
                <span className="tnum">{fmt(change)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </CxDialog>
  );
}

function PaymentRow({
  payment,
  onChange,
  onRemove,
}: {
  payment: PaymentDraft;
  onChange: (patch: Partial<PaymentDraft>) => void;
  onRemove?: () => void;
}): JSX.Element {
  const isCash = payment.method === 'CASH';
  const isQr = payment.method === 'YAPE' || payment.method === 'PLIN';
  const needsReference = payment.method === 'CARD' || payment.method === 'TRANSFER';
  const change = isCash ? Math.max(0, (payment.receivedAmount ?? 0) - payment.amount) : 0;

  return (
    <div className="card card-pad" style={{ padding: 14 }}>
      <div className="row between aic" style={{ marginBottom: 12 }}>
        <span className="label" style={{ margin: 0 }}>
          Método de pago
        </span>
        {onRemove && (
          <button className="btn btn-ghost btn-sm btn-icon" onClick={onRemove} aria-label="Quitar pago" style={{ color: 'var(--error)' }}>
            <X size={16} />
          </button>
        )}
      </div>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 12 }}>
        {METHODS.map((m) => {
          const Icon = m.icon;
          const on = payment.method === m.value;
          return (
            <button
              key={m.value}
              onClick={() => onChange({ method: m.value, receivedAmount: m.value === 'CASH' ? payment.amount : undefined, reference: undefined })}
              className="col aic center gap6"
              style={{ padding: '12px 6px', borderRadius: 11, border: on ? '1.5px solid var(--ink)' : '1px solid var(--border)', background: on ? 'var(--canvas)' : '#fff' }}
            >
              <div style={{ width: 30, height: 30, borderRadius: 8, display: 'grid', placeItems: 'center', background: `${m.color}18`, color: m.color }}>
                <Icon size={17} />
              </div>
              <span className="t-xs fw6">{m.label}</span>
            </button>
          );
        })}
      </div>

      <div className="grid" style={{ gridTemplateColumns: isCash || needsReference ? '1fr 1fr' : '1fr', gap: 12 }}>
        <div className="field">
          <label className="label">Monto a cobrar</label>
          <input
            className="input tnum"
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={payment.amount}
            onChange={(e) => onChange({ amount: Number(e.target.value) || 0, receivedAmount: isCash ? Number(e.target.value) || 0 : undefined })}
          />
        </div>
        {isCash && (
          <div className="field">
            <label className="label">Recibido</label>
            <input
              className="input tnum"
              type="number"
              inputMode="decimal"
              min={payment.amount}
              step="0.01"
              value={payment.receivedAmount ?? payment.amount}
              onChange={(e) => onChange({ receivedAmount: Number(e.target.value) || 0 })}
            />
          </div>
        )}
        {needsReference && (
          <div className="field">
            <label className="label">Referencia</label>
            <input
              className="input"
              value={payment.reference ?? ''}
              autoComplete="off"
              placeholder={payment.method === 'CARD' ? '4 últimos dígitos' : 'N° operación'}
              onChange={(e) => onChange({ reference: e.target.value })}
            />
          </div>
        )}
      </div>

      {isCash && change > 0 && (
        <div className="t-sm fw6 tnum" style={{ textAlign: 'right', color: '#15803D', marginTop: 8 }}>
          Vuelto: {fmt(change)}
        </div>
      )}

      {isQr && (
        <div className="col aic gap8" style={{ padding: 12, background: 'var(--canvas)', borderRadius: 12, marginTop: 12 }}>
          <div style={{ width: 110, height: 110, background: '#fff', border: '1px solid var(--border)', borderRadius: 12, display: 'grid', placeItems: 'center' }}>
            <QrCode size={64} style={{ color: '#111827' }} />
          </div>
          <div className="t-sm muted">Escanea para pagar con {payment.method}</div>
        </div>
      )}
    </div>
  );
}
